import React, { useId } from 'react';

/**
 * TAXFIELDS (Phase 2 of 3): the employee's own personal tax facts - Title, Marital Status, and
 * the UK Tax and HMRC block (NI/passport number, HMRC Starter Declaration, student loan, P45
 * provided, UK resident).
 *
 * Extracted from NewStarterForm.js's Section 1 so the SAME field definitions can be rendered in
 * two places without duplication:
 *   1. NewStarterForm.js Section 1 (the full new starter form)
 *   2. EmployeeView.js's "I don't have a P45" flow, where HMRC requires exactly these Starter
 *      Declaration answers as the P45 substitute
 *
 * Deliberately presentational only: it owns no state and performs no saving. The caller supplies
 * `value` plus a `set(patch)` updater and does its own persistence, so both callers write through
 * their existing save path rather than this component introducing a second one.
 *
 * No validation here - Phase 3 owns that, and it remains deferred. RequiredMark is rendered
 * exactly as it was in Section 1 (decorative today).
 */
function StarterTaxFields({ value, set, styles: s, showUKTax = true, showEmployeeDetails = true, RequiredMark }) {
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  return (
    <>
      {showEmployeeDetails && (
        <>
              {/* ── Employee Details ── */}
              <div style={s.fieldGroup}>
                <h3 style={s.groupTitle}>Employee Details</h3>
                <div style={s.row}>
                  <div style={s.field}>
                    <label style={s.label} htmlFor={`${a11yId}-title`}>Title <RequiredMark /></label>
                    
                    <select id={`${a11yId}-title`} style={s.input} value={value.title}
                      onChange={e => set({ title: e.target.value})}>
                      <option value="">Select title</option>
                      {['Mr', 'Mrs', 'Miss', 'Ms', 'Other'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label} htmlFor={`${a11yId}-marital-status`}>Marital Status <RequiredMark /></label>
                    
                    <select id={`${a11yId}-marital-status`} style={s.input} value={value.marital_status}
                      onChange={e => set({ marital_status: e.target.value})}>
                      <option value="">Select status</option>
                      {['Single', 'Married', 'Divorced', 'Other'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* ── UK Tax and HMRC Information ── */}
          
        </>
      )}

    {showUKTax && (
                <div style={s.fieldGroup}>
                  <h3 style={s.groupTitle}>UK Tax and HMRC Information</h3>

                  <div style={s.field}>
                    <label style={s.label} htmlFor={`${a11yId}-national-id-ni-number`}>National ID / NI Number</label>
                    
                    <input id={`${a11yId}-national-id-ni-number`} style={s.input} value={value.ni_number}
                      onChange={e => set({ ni_number: e.target.value})}
                      placeholder="e.g. AB123456C" />
                    <p style={s.hint}>If not available, provide the passport number below.</p>
                  </div>
                  {!value.ni_number && (
                    <div style={s.field}>
                      <label style={s.label} htmlFor={`${a11yId}-passport-number`}>Passport Number <RequiredMark /></label>
                      
                      <input id={`${a11yId}-passport-number`} style={s.input} value={value.passport_number}
                        onChange={e => set({ passport_number: e.target.value})}
                        placeholder="e.g. 123456789" />
                    </div>
                  )}

                  <div style={s.field}>
                    <label style={s.label}>Starter Declaration: tick all that apply</label>
                    <div style={{...s.checkboxGroup, marginTop: '10px'}}>
                      <label style={s.checkboxItem}>
                        <input type="checkbox" checked={value.starter_first_job_since_april}
                          onChange={e => set({ starter_first_job_since_april: e.target.checked})} />
                        This is my first job since 6th April
                      </label>
                      <label style={s.checkboxItem}>
                        <input type="checkbox" checked={value.starter_only_job}
                          onChange={e => set({ starter_only_job: e.target.checked})} />
                        This is my only job
                      </label>
                      <label style={s.checkboxItem}>
                        <input type="checkbox" checked={value.starter_other_income}
                          onChange={e => set({ starter_other_income: e.target.checked})} />
                        I have income from another job or pension
                      </label>
                    </div>
                  </div>

                  <div style={s.field}>
                    <label style={s.label}>Student loan repayment due?</label>
                    <div style={{...s.radioGroup, marginTop: '8px'}}>
                      {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                        <label key={lbl} style={s.checkboxItem}>
                          <input type="radio" name="student_loan_repayment" value={lbl}
                            checked={value.student_loan_repayment === val}
                            onChange={() => set({ student_loan_repayment: val, student_loan_plan: ''})} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                    {value.student_loan_repayment && (
                      <div style={{...s.field, marginTop: '12px', maxWidth: '240px'}}>
                        <label style={s.label} htmlFor={`${a11yId}-loan-plan`}>Loan Plan <RequiredMark /></label>
                        
                        <select id={`${a11yId}-loan-plan`} style={s.input} value={value.student_loan_plan}
                          onChange={e => set({ student_loan_plan: e.target.value})}>
                          <option value="">Select plan</option>
                          {['Plan 1', 'Plan 2', 'Plan 4', 'Plan 5', 'PGL'].map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div style={s.field}>
                    <label style={s.label}>P45 provided?</label>
                    <div style={{...s.radioGroup, marginTop: '8px'}}>
                      {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                        <label key={lbl} style={s.checkboxItem}>
                          <input type="radio" name="p45_provided" value={lbl}
                            checked={value.p45_provided === val}
                            onChange={() => set({ p45_provided: val})} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={s.field}>
                    <label style={s.label}>UK resident?</label>
                    <div style={{...s.radioGroup, marginTop: '8px'}}>
                      {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                        <label key={lbl} style={s.checkboxItem}>
                          <input type="radio" name="uk_resident" value={lbl}
                            checked={value.uk_resident === val}
                            onChange={() => set({ uk_resident: val})} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
    </>
  );
}

export default StarterTaxFields;
